import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import { ArrowBackIosNew, ArrowForwardIos } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useLocation } from 'react-router-dom';
import {
  fetchAllEmployees,
  fetchEmployeeStatsDailyRange,
  fetchEmployeeStatsMonthlyForYear,
  fetchBusinessStatsDailyRange,
  fetchBusinessStatsMonthlyForYear,
  getBusinessConfigById,
  getImageFromStorage,
} from '../../firebase/firebase-delegate';
import { BusinessConfig, Employee, StatsDocNumbers } from '../../types';
import { dayjs, Dayjs } from '../../utils/dayjs-config';
import { getCachedAssetUrl } from '../../utils/assetCache';
import { resolveBookingUxConfig } from '../../utils/bookingUxConfig';

type ViewMode = 'week' | 'month';
type Metric = 'bookings' | 'revenue' | 'duration';
const ALL_EMPLOYEES_OPTION = 'ALL_EMPLOYEES';
const ALL_BUSINESSES_OPTION = 'ALL_BUSINESSES';

type PeriodRow = {
  key: string;
  label: string;
  numbers: StatsDocNumbers;
};

type ChartRow = {
  key: string;
  label: string;
  completed: number;
  canceled: number;
  total: number;
};

type SelectedBar = {
  label: string;
  completed: number;
  canceled: number;
  total: number;
  metric: Metric;
};

type SuperAdminAnalyticsLocationState = {
  websiteIds?: string[];
};

const DEFAULT_TIMEZONE = 'America/New_York';

const zeroStats: StatsDocNumbers = {
  upcomingCount: 0,
  completedCount: 0,
  canceledCount: 0,
  upcomingRevenueCents: 0,
  completedRevenueCents: 0,
  canceledRevenueCents: 0,
  upcomingMinutes: 0,
  completedMinutes: 0,
  canceledMinutes: 0,
};

const addStatsNumbers = (base: StatsDocNumbers, incoming: StatsDocNumbers): StatsDocNumbers => ({
  upcomingCount: base.upcomingCount + incoming.upcomingCount,
  completedCount: base.completedCount + incoming.completedCount,
  canceledCount: base.canceledCount + incoming.canceledCount,
  upcomingRevenueCents: base.upcomingRevenueCents + incoming.upcomingRevenueCents,
  completedRevenueCents: base.completedRevenueCents + incoming.completedRevenueCents,
  canceledRevenueCents: base.canceledRevenueCents + incoming.canceledRevenueCents,
  upcomingMinutes: base.upcomingMinutes + incoming.upcomingMinutes,
  completedMinutes: base.completedMinutes + incoming.completedMinutes,
  canceledMinutes: base.canceledMinutes + incoming.canceledMinutes,
});

const mergeStatsMaps = (maps: Record<string, StatsDocNumbers>[]): Record<string, StatsDocNumbers> => {
  const merged: Record<string, StatsDocNumbers> = {};
  maps.forEach((statsMap) => {
    Object.entries(statsMap).forEach(([key, numbers]) => {
      merged[key] = addStatsNumbers(merged[key] ?? zeroStats, numbers);
    });
  });
  return merged;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

const formatDuration = (minutes: number) => {
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hrs > 0) {
    return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
  }
  return `${mins}m`;
};

const formatMetricValue = (metric: Metric, value: number) => {
  if (metric === 'revenue') return formatCurrency(value);
  if (metric === 'duration') return formatDuration(value);
  return `${value}`;
};

const getEmployeeInitials = (employee: Employee) =>
  `${employee.firstName?.[0] ?? ''}${employee.lastName?.[0] ?? ''}`.toUpperCase() || 'E';

const getWeekStart = (anchor: Dayjs) => anchor.tz(DEFAULT_TIMEZONE).startOf('week');

const SuperAdminAnalyticsPage: React.FC = () => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const location = useLocation();
  const locationState = (location.state as SuperAdminAnalyticsLocationState | undefined) ?? undefined;
  const websiteIds = useMemo(
    () => {
      const ids = locationState?.websiteIds;
      return Array.isArray(ids) ? ids : [];
    },
    [locationState]
  );
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>(ALL_EMPLOYEES_OPTION);
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [metric, setMetric] = useState<Metric>('bookings');
  const [weekAnchor, setWeekAnchor] = useState<Dayjs>(() => getWeekStart(dayjs.tz(undefined, DEFAULT_TIMEZONE)));
  const [monthAnchor, setMonthAnchor] = useState<Dayjs>(() => dayjs.tz(undefined, DEFAULT_TIMEZONE).startOf('year'));
  const [weekPickerOpen, setWeekPickerOpen] = useState(false);
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [selectedBar, setSelectedBar] = useState<SelectedBar | null>(null);
  const rangeAnchorRef = useRef<HTMLDivElement | null>(null);
  const [profilePictures, setProfilePictures] = useState<Record<string, string>>({});
  const websiteIdsKey = useMemo(() => websiteIds.slice().sort().join('|'), [websiteIds]);
  const isAllBusinesses = selectedSiteId === ALL_BUSINESSES_OPTION;

  useEffect(() => {
    if (!selectedSiteId && websiteIds.length > 0) {
      setSelectedSiteId(ALL_BUSINESSES_OPTION);
    }
  }, [selectedSiteId, websiteIds]);

  useEffect(() => {
    if (
      selectedSiteId &&
      selectedSiteId !== ALL_BUSINESSES_OPTION &&
      websiteIds.length > 0 &&
      !websiteIds.includes(selectedSiteId)
    ) {
      setSelectedSiteId(ALL_BUSINESSES_OPTION);
    }
  }, [selectedSiteId, websiteIds]);

  const employeesQuery = useQuery<Employee[]>({
    queryKey: ['superadmin', 'employees', selectedSiteId],
    queryFn: () => fetchAllEmployees(selectedSiteId),
    enabled: !!selectedSiteId && !isAllBusinesses,
  });

  const employees = useMemo(() => employeesQuery.data ?? [], [employeesQuery.data]);
  const employeeId = selectedEmployeeId;
  const businessConfigQuery = useQuery<BusinessConfig | null>({
    queryKey: ['businessConfig', selectedSiteId],
    enabled: !!selectedSiteId && selectedSiteId !== ALL_BUSINESSES_OPTION,
    queryFn: () => getBusinessConfigById(selectedSiteId),
  });
  const bookingUxConfig = resolveBookingUxConfig(businessConfigQuery.data);
  const resourceSingular = bookingUxConfig.terminology.resourceSingular;
  const resourcePlural = bookingUxConfig.terminology.resourcePlural;

  useEffect(() => {
    setSelectedEmployeeId(ALL_EMPLOYEES_OPTION);
    setProfilePictures({});
  }, [selectedSiteId]);

  useEffect(() => {
    if (!employeeId) {
      setSelectedEmployeeId(ALL_EMPLOYEES_OPTION);
      return;
    }
    if (employeeId === ALL_EMPLOYEES_OPTION) {
      return;
    }
    const employeeExists = employees.some((employee) => employee.id === employeeId);
    if (!employeeExists) {
      setSelectedEmployeeId(ALL_EMPLOYEES_OPTION);
    }
  }, [employeeId, employees]);

  useEffect(() => {
    let isMounted = true;
    const fetchProfilePictures = async () => {
      if (employees.length === 0) {
        setProfilePictures({});
        return;
      }

      const newPictures: Record<string, string> = {};
      await Promise.all(
        employees.map(async (employee) => {
          const picturePath = employee.profilePictureImagePath;
          if (!picturePath) return;
          try {
            const url = await getCachedAssetUrl(
              picturePath,
              () => getImageFromStorage(picturePath)
            );
            if (url) {
              newPictures[employee.id] = url;
            }
          } catch (error) {
            console.error('Failed to load employee profile picture', error);
          }
        })
      );

      if (isMounted) {
        setProfilePictures(newPictures);
      }
    };

    fetchProfilePictures();
    return () => {
      isMounted = false;
    };
  }, [employees]);

  const weekStart = useMemo(() => getWeekStart(weekAnchor), [weekAnchor]);
  const weekEnd = useMemo(() => weekStart.add(6, 'day'), [weekStart]);
  const weekKeys = useMemo(
    () => Array.from({ length: 7 }, (_, idx) => weekStart.add(idx, 'day').format('YYYYMMDD')),
    [weekStart]
  );

  const selectedYear = monthAnchor.year();
  const maxMonths = 12;

  const isAllEmployees = employeeId === ALL_EMPLOYEES_OPTION;
  const targetSiteIds = useMemo(
    () => (isAllBusinesses ? websiteIds : selectedSiteId ? [selectedSiteId] : []),
    [isAllBusinesses, selectedSiteId, websiteIds]
  );
  const shouldUseBusinessTotals = isAllBusinesses || isAllEmployees;

  const weeklyStatsQuery = useQuery({
    queryKey: [
      'superadmin-analytics',
      selectedSiteId,
      websiteIdsKey,
      shouldUseBusinessTotals ? 'business' : 'employee',
      employeeId,
      'week',
      weekStart.format('YYYYMMDD'),
    ],
    enabled: viewMode === 'week' && targetSiteIds.length > 0 && (!!employeeId || shouldUseBusinessTotals),
    queryFn: async () => {
      if (shouldUseBusinessTotals) {
        const statsMaps = await Promise.all(
          targetSiteIds.map((siteId) => fetchBusinessStatsDailyRange(weekKeys[0], weekKeys[6], siteId))
        );
        return mergeStatsMaps(statsMaps);
      }
      return fetchEmployeeStatsDailyRange(employeeId, weekKeys[0], weekKeys[6], selectedSiteId);
    },
  });

  const monthlyStatsQuery = useQuery({
    queryKey: [
      'superadmin-analytics',
      selectedSiteId,
      websiteIdsKey,
      shouldUseBusinessTotals ? 'business' : 'employee',
      employeeId,
      'monthly',
      selectedYear,
    ],
    enabled: viewMode === 'month' && targetSiteIds.length > 0 && (!!employeeId || shouldUseBusinessTotals),
    queryFn: async () => {
      if (shouldUseBusinessTotals) {
        const statsMaps = await Promise.all(
          targetSiteIds.map((siteId) => fetchBusinessStatsMonthlyForYear(selectedYear, siteId))
        );
        return mergeStatsMaps(statsMaps);
      }
      return fetchEmployeeStatsMonthlyForYear(employeeId, selectedYear, selectedSiteId);
    },
  });

  const weeklyRows: PeriodRow[] = useMemo(() => {
    if (!employeeId) return [];
    const statsMap = weeklyStatsQuery.data ?? {};
    return weekKeys.map((key, idx) => ({
      key,
      label: weekStart.add(idx, 'day').format('ddd, MMM D'),
      numbers: statsMap[key] ?? zeroStats,
    }));
  }, [employeeId, weekKeys, weekStart, weeklyStatsQuery.data]);

  const monthlyRows: PeriodRow[] = useMemo(() => {
    if (!employeeId) return [];
    const statsMap = monthlyStatsQuery.data ?? {};
    const rows: PeriodRow[] = [];
    for (let m = 1; m <= maxMonths; m++) {
      const key = `${selectedYear}${String(m).padStart(2, '0')}`;
      const monthLabel = dayjs(`${selectedYear}-${String(m).padStart(2, '0')}-01`).format('MMM');
      rows.push({
        key,
        label: monthLabel,
        numbers: statsMap[key] ?? zeroStats,
      });
    }
    return rows;
  }, [employeeId, monthlyStatsQuery.data, maxMonths, selectedYear]);

  const metricRows: ChartRow[] = useMemo(() => {
    const sourceRows = viewMode === 'week' ? weeklyRows : monthlyRows;
    return sourceRows.map((row) => {
      const { numbers } = row;
      const completed =
        metric === 'bookings'
          ? numbers.completedCount
          : metric === 'revenue'
            ? numbers.completedRevenueCents / 100
            : numbers.completedMinutes;
      const canceled =
        metric === 'bookings'
          ? numbers.canceledCount
          : metric === 'revenue'
            ? numbers.canceledRevenueCents / 100
            : numbers.canceledMinutes;
      return {
        key: row.key,
        label: row.label,
        completed,
        canceled,
        total: completed + canceled,
      };
    });
  }, [metric, monthlyRows, viewMode, weeklyRows]);

  const maxValue = useMemo(() => {
    if (!metricRows.length) return 0;
    const max = Math.max(...metricRows.map((row) => row.total));
    return max > 0 ? max : 0;
  }, [metricRows]);

  const maxCompletedValue = useMemo(() => {
    if (!metricRows.length) return 0;
    const maxCompleted = Math.max(...metricRows.map((row) => row.completed));
    return maxCompleted > 0 ? maxCompleted : 0;
  }, [metricRows]);

  const yearlyTotals = useMemo(() => {
    return metricRows.reduce(
      (acc, row) => {
        acc.completed += row.completed;
        acc.canceled += row.canceled;
        acc.total += row.total;
        return acc;
      },
      { completed: 0, canceled: 0, total: 0 }
    );
  }, [metricRows]);

  const maxLabel =
    metric === 'bookings'
      ? `${maxCompletedValue}`
      : metric === 'revenue'
        ? formatCurrency(maxCompletedValue)
        : formatDuration(maxCompletedValue);

  const isLoadingStats =
    employeesQuery.isLoading ||
    (viewMode === 'week' ? weeklyStatsQuery.isLoading || weeklyStatsQuery.isFetching : monthlyStatsQuery.isLoading || monthlyStatsQuery.isFetching);

  const rangeLabel =
    viewMode === 'week'
      ? isMdUp
        ? `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D, YYYY')}`
        : `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D')}`
      : `${selectedYear}`;

  const metricLabel = metric === 'bookings' ? 'Bookings' : metric === 'revenue' ? 'Revenue' : 'Duration';

  const handlePrev = () => {
    if (viewMode === 'week') {
      setWeekAnchor((prev) => getWeekStart(prev.subtract(1, 'week')));
    } else {
      setMonthAnchor((prev) => prev.subtract(1, 'year').startOf('year'));
    }
  };

  const handleNext = () => {
    if (viewMode === 'week') {
      setWeekAnchor((prev) => getWeekStart(prev.add(1, 'week')));
    } else {
      setMonthAnchor((prev) => prev.add(1, 'year').startOf('year'));
    }
  };

  const handleToday = () => {
    setViewMode('week');
    setWeekAnchor(getWeekStart(dayjs.tz(undefined, DEFAULT_TIMEZONE)));
  };

  const handleToggleMode = () => {
    if (viewMode === 'week') {
      setViewMode('month');
      setMonthAnchor(dayjs.tz(undefined, DEFAULT_TIMEZONE).startOf('year'));
      return;
    }
    setViewMode('week');
    setWeekAnchor(getWeekStart(dayjs.tz(undefined, DEFAULT_TIMEZONE)));
  };

  const handleBarClick = (row: ChartRow) => {
    setSelectedBar({
      label: row.label,
      completed: row.completed,
      canceled: row.canceled,
      total: row.total,
      metric,
    });
  };

  const showSiteSelector = websiteIds.length > 0;
  const showEmployeeSelector = !!selectedSiteId && !isAllBusinesses;

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
      <Stack spacing={2}>
        {showSiteSelector && (
          <Box
            display="flex"
            justifyContent={{ xs: 'center', md: 'flex-start' }}
          >
            <TextField
              select
              size="small"
              label="Business"
              value={selectedSiteId}
              onChange={(event) => setSelectedSiteId(event.target.value)}
              sx={{
                maxWidth: 320,
                width: '100%',
                bgcolor: 'background.paper',
                borderRadius: 1.5,
                boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  minHeight: 36,
                  '& fieldset': { borderColor: 'divider' },
                  '&:hover fieldset': { borderColor: 'text.secondary' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1.5 },
                },
                '& .MuiInputLabel-root': { fontWeight: 600, fontSize: '0.95rem' },
                '& .MuiSelect-select': { fontSize: '0.98rem' },
                '& .MuiSelect-icon': { color: 'text.secondary' },
              }}
            >
              <MenuItem value={ALL_BUSINESSES_OPTION}>
                <Typography variant="body1" fontWeight={600}>
                  All Businesses
                </Typography>
              </MenuItem>
              {websiteIds.map((siteId) => (
                <MenuItem key={siteId} value={siteId}>
                  <Typography variant="body1" fontWeight={600}>
                    {siteId}
                  </Typography>
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {showEmployeeSelector && (
          <Box
            display="flex"
            justifyContent={{ xs: 'center', md: 'flex-start' }}
          >
            <TextField
              select
              size="small"
              label={resourceSingular}
              value={employeeId}
              onChange={(event) => setSelectedEmployeeId(event.target.value)}
              SelectProps={{
                renderValue: (value) => {
                  if (!value) {
                    return (
                      <Typography variant="body1" color="text.secondary">
                        {`Select ${resourceSingular.toLowerCase()}`}
                      </Typography>
                    );
                  }
                  const id = value as string;
                  const isAll = id === ALL_EMPLOYEES_OPTION;
                  const employee = employees.find((item) => item.id === id);
                  const avatarSrc = !isAll && employee ? profilePictures[employee.id] : undefined;
                  const label = isAll
                    ? `All ${resourcePlural}`
                    : employee
                      ? `${employee.firstName} ${employee.lastName}`
                      : '';

                  return (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {!isAll && (
                        <Avatar
                          src={avatarSrc}
                          sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'grey.300', color: 'text.primary' }}
                        >
                          {employee ? getEmployeeInitials(employee) : ''}
                        </Avatar>
                      )}
                      <Typography variant="body1" fontWeight={600}>
                        {label}
                      </Typography>
                    </Stack>
                  );
                },
              }}
              sx={{
                maxWidth: 320,
                width: '100%',
                bgcolor: 'background.paper',
                borderRadius: 1.5,
                boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  minHeight: 36,
                  '& fieldset': { borderColor: 'divider' },
                  '&:hover fieldset': { borderColor: 'text.secondary' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1.5 },
                },
                '& .MuiInputLabel-root': { fontWeight: 600, fontSize: '0.95rem' },
                '& .MuiSelect-select': { fontSize: '0.98rem' },
                '& .MuiSelect-icon': { color: 'text.secondary' },
              }}
            >
              <MenuItem value={ALL_EMPLOYEES_OPTION}>
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <Typography variant="body1" fontWeight={600}>
                    {`All ${resourcePlural}`}
                  </Typography>
                </Stack>
              </MenuItem>
              {employees.map((employee) => (
                <MenuItem key={employee.id} value={employee.id}>
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Avatar
                      src={profilePictures[employee.id]}
                      sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'grey.300', color: 'text.primary' }}
                    >
                      {getEmployeeInitials(employee)}
                    </Avatar>
                    <Typography variant="body1" fontWeight={600}>
                      {employee.firstName} {employee.lastName}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}
        {showEmployeeSelector && <Box sx={{ height: { xs: 4, md: 8 } }} />}

        {isSmUp ? (
          <Box position="relative" display="flex" alignItems="center" width="100%">
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="center"
              sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}
            >
              <IconButton onClick={handlePrev} aria-label="previous period" color="primary">
                <ArrowBackIosNew fontSize="small" />
              </IconButton>
              <Box
                onClick={() => (viewMode === 'week' ? setWeekPickerOpen(true) : setMonthPickerOpen(true))}
                sx={{
                  cursor: 'pointer',
                  px: 0,
                  py: 0,
                  color: 'text.primary',
                }}
                ref={rangeAnchorRef}
              >
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ fontSize: { xs: '1.05rem', sm: '1.2rem', md: '1.2rem' }, lineHeight: 1.2 }}
                >
                  {rangeLabel}
                </Typography>
              </Box>
              <IconButton onClick={handleNext} aria-label="next period" color="primary">
                <ArrowForwardIos fontSize="small" />
              </IconButton>
            </Stack>

            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              sx={{ marginLeft: 'auto', columnGap: 1 }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={handleToday}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' },
                  px: { xs: 1.25, md: 2 },
                }}
              >
                Today
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleToggleMode}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' },
                  px: { xs: 1.25, md: 2 },
                }}
              >
                {viewMode === 'month' ? 'Week' : 'Year'}
              </Button>
            </Box>
          </Box>
        ) : (
          <Stack direction="row" spacing={0} alignItems="center" justifyContent="space-between" width="100%">
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton onClick={handlePrev} aria-label="previous period" color="primary">
                <ArrowBackIosNew fontSize="small" />
              </IconButton>
              <Box
                onClick={() => (viewMode === 'week' ? setWeekPickerOpen(true) : setMonthPickerOpen(true))}
                sx={{
                  cursor: 'pointer',
                  px: 0,
                  py: 0,
                  color: 'text.primary',
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ fontSize: { xs: '1.05rem', sm: '1.2rem', md: '1.2rem' }, lineHeight: 1.2 }}
                >
                  {rangeLabel}
                </Typography>
              </Box>
              <IconButton onClick={handleNext} aria-label="next period" color="primary">
                <ArrowForwardIos fontSize="small" />
              </IconButton>
            </Stack>
            <Box display="flex" alignItems="center" justifyContent="flex-end" sx={{ columnGap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleToday}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' },
                  px: { xs: 1.25, md: 2 },
                }}
              >
                Today
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleToggleMode}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' },
                  px: { xs: 1.25, md: 2 },
                }}
              >
                {viewMode === 'month' ? 'Week' : 'Year'}
              </Button>
            </Box>
          </Stack>
        )}

        <Divider sx={{ my: 1 }} />

        <Tabs
          value={metric}
          onChange={(_, next) => setMetric(next as Metric)}
          centered
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab value="bookings" label="Bookings" />
          <Tab value="revenue" label="Revenue" />
          <Tab value="duration" label="Duration" />
        </Tabs>

        {metricRows.length > 0 && (
          <Typography variant="subtitle2" color="text.secondary" sx={{ textAlign: 'left', mt: 1 }}>
            Most {metric === 'bookings' ? 'Bookings' : metric === 'revenue' ? 'Revenue' : 'Duration'} This{' '}
            {viewMode === 'week' ? 'Week' : 'Year'}:{' '}
            <Typography component="span" fontWeight={700}>
              {maxLabel}
            </Typography>
          </Typography>
        )}

        {(weeklyStatsQuery.isError || monthlyStatsQuery.isError) && (
          <Alert severity="error">Failed to load analytics data. Please try again.</Alert>
        )}

        {!websiteIds.length && (
          <Alert severity="warning">No business IDs were provided. Open this page from the SuperAdmin Dashboard.</Alert>
        )}

        {isLoadingStats && (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading analytics…
            </Typography>
          </Box>
        )}

        {!selectedSiteId && (
          <Alert severity="info">Select a business to view analytics.</Alert>
        )}

        {metricRows.map((row) => (
          <BarRow
            key={row.key}
            row={row}
            maxValue={maxValue}
            metric={metric}
            onClick={() => handleBarClick(row)}
          />
        ))}

        {viewMode === 'month' && metricRows.length > 0 && (
          <Box sx={{ pt: 1 }}>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              Total Completed This Year: {formatMetricValue(metric, yearlyTotals.completed)}
            </Typography>
            <Typography variant="subtitle1" fontWeight={700}>
              Total Canceled This Year: {formatMetricValue(metric, yearlyTotals.canceled)}
            </Typography>
          </Box>
        )}

        {!metricRows.length && !isLoadingStats && employeeId && (
          <Alert severity="info">No analytics data for this period.</Alert>
        )}
      </Stack>

      <DatePicker
        open={weekPickerOpen}
        onClose={() => setWeekPickerOpen(false)}
        value={weekStart}
        onChange={(next) => {
          if (next) {
            setWeekAnchor(getWeekStart(next.tz(DEFAULT_TIMEZONE)));
            setViewMode('week');
          }
          setWeekPickerOpen(false);
        }}
        slotProps={{
          textField: { sx: { display: 'none' } },
          popper: {
            anchorEl: rangeAnchorRef.current,
            placement: 'bottom-start',
          },
        }}
      />
      <DatePicker
        views={['year']}
        open={monthPickerOpen}
        onClose={() => setMonthPickerOpen(false)}
        value={monthAnchor}
        onChange={(next) => {
          if (next) {
            setMonthAnchor(next.tz(DEFAULT_TIMEZONE).startOf('year'));
            setViewMode('month');
          }
          setMonthPickerOpen(false);
        }}
        slotProps={{
          textField: { sx: { display: 'none' } },
          popper: {
            anchorEl: rangeAnchorRef.current,
            placement: 'bottom-start',
          },
        }}
      />

      <Dialog open={!!selectedBar} onClose={() => setSelectedBar(null)} fullWidth maxWidth="xs">
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 0,
            pl: 2,
            pt: 1.5,
            pb: 1,
          }}
        >
          <Typography variant="subtitle1">
            {metricLabel} — {selectedBar?.label}
          </Typography>
          <IconButton
            onClick={() => setSelectedBar(null)}
            sx={{
              width: 32,
              height: 32,
              p: 0.5,
              mr: 0.5,
              borderRadius: '50%',
            }}
          >
            ×
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 1 }} />
          <Stack spacing={1} mt={1}>
            <SummaryRow label="Completed" color="success.main" value={formatMetricValue(metric, selectedBar?.completed ?? 0)} />
            <SummaryRow label="Canceled" color="error.main" value={formatMetricValue(metric, selectedBar?.canceled ?? 0)} />
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const SummaryRow: React.FC<{ label: string; value: string; color: string; bold?: boolean }> = ({
  label,
  value,
  color,
  bold = false,
}) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Typography variant="body2" color={color} fontWeight={bold ? 700 : 500}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={bold ? 700 : 500}>
      {value}
    </Typography>
  </Stack>
);

const BarRow: React.FC<{ row: ChartRow; maxValue: number; metric: Metric; onClick: () => void }> = ({
  row,
  maxValue,
  metric,
  onClick,
}) => {
  const completedLabel =
    row.total > 0
      ? formatMetricValue(metric, row.completed)
      : '';
  const safeMax = maxValue > 0 ? maxValue : 1;
  const fillPct = row.total > 0 ? Math.min(100, Math.max(0, (row.total / safeMax) * 100)) : 0;
  const completedPct = row.total > 0 ? Math.min(100, Math.max(0, (row.completed / row.total) * 100)) : 0;
  const canceledPct = row.total > 0 ? Math.min(100, Math.max(0, (row.canceled / row.total) * 100)) : 0;
  const clickable = row.total > 0 && maxValue > 0;
  const barFlex = row.total > 0 ? row.total : 0;
  return (
    <Box
      display="flex"
      alignItems="center"
      gap={2}
      onClick={clickable ? onClick : undefined}
      sx={{
        cursor: clickable ? 'pointer' : 'default',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        width: '100%',
        '&:hover .bar-track': clickable ? { boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.light}` } : {},
        '&:active': clickable ? { backgroundColor: 'action.hover' } : {},
        '&:focus-visible': clickable ? { outline: 'none', backgroundColor: 'action.hover' } : { outline: 'none' },
        WebkitTapHighlightColor: 'transparent',
      }}
      tabIndex={clickable ? 0 : -1}
    >
      <Box sx={{ minWidth: 80, maxWidth: 120, textAlign: 'right' }}>
        <Typography variant="body2" color="text.secondary">
          {row.label}
        </Typography>
      </Box>
      <Box flexGrow={1} minWidth={0} display="flex" alignItems="center" minHeight={36}>
        <Box
          sx={{
            height: 36,
            borderRadius: 1,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'action.hover',
          }}
        >
          {fillPct > 0 && (
            <Box
              className="bar-track"
              sx={{
                height: '100%',
                display: 'flex',
                width: `${fillPct}%`,
                borderRight: clickable ? '1px solid rgba(0,0,0,0.08)' : '1px dashed rgba(0,0,0,0.08)',
                minWidth: barFlex > 0 ? 'auto' : 0,
              }}
            >
              <Box sx={{ width: '100%', height: '100%', display: 'flex' }}>
                {completedPct > 0 && <Box sx={{ width: `${completedPct}%`, bgcolor: 'success.main' }} />}
                {canceledPct > 0 && <Box sx={{ width: `${canceledPct}%`, bgcolor: 'error.main' }} />}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={{ minWidth: 60, maxWidth: 80, textAlign: 'right' }}>
        {completedLabel && (
          <Typography variant="body2" fontWeight={600} color="success.main">
            {completedLabel}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default SuperAdminAnalyticsPage;
